import { computed, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useAppStore } from '~/store/app'
import { useBorrowerStore } from '~/store/borrower'
import { useDocumentsStore } from '~/store/documents'
import { useBankConnectorStore } from '~/store/bank-connector'
import { useRootStore } from '~/store/root'
import { useAppAnalyticsStore } from '~/store/app-analytics'
import { useApprovalsStore } from '~/store/approvals'
import { useApplicationConfigStore } from '~/store/application-config'
import { useBankPullStatusUiStore } from '~/store/bank-pull-status-ui'
import { sessionStorageService } from '~/libs/session-storage.service'

export function useMissingStatementsBanner() {
  const appStore = useAppStore()
  const borrowerStore = useBorrowerStore()
  const documentsStore = useDocumentsStore()
  const bankConnectorStore = useBankConnectorStore()
  const appAnalyticsStore = useAppAnalyticsStore()
  const approvalsStore = useApprovalsStore()
  const applicationConfigStore = useApplicationConfigStore()
  const bankPullStatusUiStore = useBankPullStatusUiStore()
  const { isEmbedded } = storeToRefs(useRootStore())

  const dataLoaded = ref(false)

  async function loadAlertData() {
    if (!borrowerStore.borrowerId) return
    try {
      await Promise.all([
        // Forced: an unforced call short-circuits on an already-populated cache, leaving
        // hasSyncedAllFinicityStatements stale-false so the banner reports a failed pull for
        // statements that have since synced.
        documentsStore.getDocuments(true),
        bankConnectorStore.getBanks(),
      ])
    } catch (err) {
      log.error('useMissingStatementsBanner failed to load alert data:', err)
    } finally {
      dataLoaded.value = true
    }
  }

  onMounted(loadAlertData)
  watch(() => borrowerStore.borrowerId, (id) => {
    if (id) loadAlertData()
  })

  // null | 'failed' | 'pending' - which banner variant (if any) should show. Restored to its
  // original guard chain (mpApplicationComplete, approvals, hasRequiredBankStatementsManual) -
  // a prior pass had delegated this to the shared bank-pull-status-ui store's promptEligible
  // getter, which deliberately drops the approvals guard (correct for the new chip/toast, which
  // should still surface a real unresolved failure to an approved borrower - wrong for this
  // banner, which must keep its own pre-existing, approvals-gated behavior unchanged for
  // borrowers outside the experiment).
  const bannerState = computed(() => {
    if (!dataLoaded.value) return null

    if (!borrowerStore.mpApplicationComplete) return null

    if (approvalsStore.approvals.length) return null

    if (documentsStore.hasRequiredBankStatementsManual) return null

    // The dashboard upload card decides on this same store derivation behind the same
    // short-circuit guards as above, so the two prompting surfaces agree on what the pull
    // status means. It is null when bank-service has told us nothing yet or no checking
    // account is connected to pull from - both mean "say nothing".
    const promptState = bankConnectorStore.statementPullPromptState

    // For a borrower in the bank-pull-status experiment the chip/popover/toast cover a failed
    // pull, so showing this banner too would be redundant - but only once the chip is actually
    // showing 'failed'. promptState is 'failed' for a null retrieval state too (bank-service
    // reported nothing on the eligible accounts and the statements are not on file, e.g. a
    // borrower back after the 24h correlation window), and the chip deliberately stays silent for
    // null, so suppressing on promptState alone would leave that borrower with no failure signal
    // at all. Borrowers outside the experiment have no other failure signal either, so this
    // banner keeps covering them exactly as it did before that feature shipped.
    if (
      promptState === 'failed'
      && applicationConfigStore.isBankPullStatusIndicatorEnabled
      && bankPullStatusUiStore.indicatorState === 'failed'
    ) return null

    return promptState === 'completed' ? null : promptState
  })

  const showMissingStatements = computed(() => bannerState.value === 'failed')

  // Mirrors bannerState's guards, but reports the raw retrieval state rather than the
  // no-report-means-failed substitution, so the metric stays honest about what bank-service
  // actually said.
  const statementPullStatusForAnalytics = computed(() => {
    if (!bankConnectorStore.statementPullStatus) return null
    if (!bankConnectorStore.checkingAccounts.length) return null
    return bankConnectorStore.statementPullRetrievalState
  })

  const pendingBannerMessage = computed(() => {
    const checkingAccountsCount = bankConnectorStore.checkingAccounts.length
    return `We're still pulling bank statements from your connected account${checkingAccountsCount > 1 ? 's.' : '.'}`
  })

  // Piggybacks on the bannerState watch below rather than adding a new watcher.
  function trackStatementPullStatus() {
    const status = statementPullStatusForAnalytics.value
    if (!status) return
    const key = `fs_statement_pull_status_portal_${status}`
    if (sessionStorageService.getItem(key)) return
    sessionStorageService.setItem(key, '1')
    appAnalyticsStore.fsTrackEvent({ name: 'Statement Pull Status', properties: { status, page: 'portal' } })
  }

  watch(bannerState, (state) => {
    if (state === 'failed') {
      const dashboardUrl = `${isEmbedded.value ? '/embedded' : ''}/portal#missing-statements-upload`
      appStore.addBanner({
        type: 'warning',
        title: 'Bank statements pull failed',
        msg: `Your bank connection came through, but a few statements didn't transfer over. Upload them on the dashboard and we'll take it from there.`,
        href: dashboardUrl,
        linkText: 'Upload statements',
        label: 'finicityBanner',
      })
      // Temp monitor for release. Can remove later if not needed.
      appAnalyticsStore.fsTrackEvent({ name: 'Missing Documents Banner' })
    } else if (state === 'pending') {
      appStore.addBanner({
        title: 'Bank statements pull in progress',
        type: 'info',
        msg: pendingBannerMessage.value,
        label: 'finicityBanner',
      })
    } else {
        appStore.clearBanner('finicityBanner')
    }

    trackStatementPullStatus()
  }, { immediate: true })

  return { showMissingStatements }
}
